/**
 * « De qui regarde-t-on les saisies ? »
 *
 * Extrait des deux copies qui vivaient dans `CalendarView` et
 * `CalendarToolbar` ; le tracker et la feuille de temps en auraient fait une
 * troisième et une quatrième.
 *
 * Les options ne listent que les comptes actifs, mais le libellé du
 * sélectionné se cherche dans la liste COMPLÈTE : un salarié désactivé après
 * coup garde son nom au lieu de retomber sur un champ vide.
 */
import { Autocomplete, TextField } from '@mui/material';
import type { User } from '@altyor-kronos/shared-types';

interface Option {
  id: string;
  label: string;
}

interface Props {
  /** Liste complète, actifs et inactifs. Le filtrage se fait ici. */
  users: ReadonlyArray<{ id: string; data: User }>;
  /** uid sélectionné, ou '' pour « moi ». */
  value: string;
  onChange: (uid: string) => void;
  placeholder: string;
  width?: number;
}

const toOption = (u: { id: string; data: User }): Option => ({
  id: u.id,
  label: u.data.displayName || u.data.email,
});

export default function TeammateScopePicker({
  users,
  value,
  onChange,
  placeholder,
  width = 220,
}: Props) {
  const options = users.filter((u) => u.data.active !== false).map(toOption);
  const selected = users.map(toOption).find((o) => o.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={options}
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? '')}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} />}
      data-testid="teammate-scope-picker"
    />
  );
}
